// AffiliateTracker.js - Handle affiliate links and attribution tracking

import { db } from '../../services/firebase';
import { collection, addDoc, doc, updateDoc, increment, getDoc } from 'firebase/firestore';

export class AffiliateTracker {
  static AFFILIATE_IDS = {
    amazon: 'artifex-20', // Replace with your Amazon affiliate ID
    commission_junction: 'your-cj-id', // Replace with your CJ ID
    etsy: 'your-etsy-id' // If Etsy has affiliate program
  };

  static async wrapAffiliateLink(originalUrl, contributorId, productId, source = 'shop') {
    try {
      // Detect platform and add appropriate affiliate codes
      const wrappedUrl = this.addAffiliateCode(originalUrl);
      
      // Generate tracking ID
      const trackingId = this.generateTrackingId();
      
      // Store tracking data
      await addDoc(collection(db, 'linkTracking'), {
        trackingId,
        originalUrl,
        wrappedUrl,
        contributorId,
        productId,
        source, // 'shop', 'editorial', 'signals'
        clicks: 0,
        conversions: 0,
        revenue: 0,
        createdAt: new Date(),
        lastClickedAt: null
      });

      // Return the trackable URL
      return `${window.location.origin}/track/${trackingId}`;
      
    } catch (error) {
      console.error('Error wrapping affiliate link:', error);
      return originalUrl; // Fallback to original URL
    }
  }

  static addAffiliateCode(url) {
    try {
      const urlObj = new URL(url);
      const domain = urlObj.hostname.toLowerCase();

      // Amazon affiliate links
      if (domain.includes('amazon.')) {
        // Remove existing affiliate tags
        urlObj.searchParams.delete('tag');
        urlObj.searchParams.delete('linkCode');
        urlObj.searchParams.delete('ref');
        
        // Add Artifex affiliate tag
        urlObj.searchParams.set('tag', this.AFFILIATE_IDS.amazon);
        urlObj.searchParams.set('linkCode', 'as2');
        urlObj.searchParams.set('ref', 'artifex');
        
        return urlObj.toString();
      }

      // Etsy links (if they have affiliate program)
      if (domain.includes('etsy.com')) {
        urlObj.searchParams.set('ref', 'artifex');
        return urlObj.toString();
      }

      // Commission Junction deep links
      if (this.isCJEligible(domain)) {
        return this.generateCJDeepLink(url);
      }

      // For other URLs, just return as-is but still track
      return url;
      
    } catch (error) {
      console.error('Error adding affiliate code:', error);
      return url;
    }
  }

  static isCJEligible(domain) {
    // List of domains that participate in Commission Junction
    const cjDomains = [
      'wayfair.com',
      'overstock.com',
      'anthropologie.com',
      'westelm.com',
      'cb2.com',
      'crateandbarrel.com'
    ];
    
    return cjDomains.some(cjDomain => domain.includes(cjDomain));
  }

  static generateCJDeepLink(originalUrl) {
    // Commission Junction deep link format
    const cjId = this.AFFILIATE_IDS.commission_junction;
    const encodedUrl = encodeURIComponent(originalUrl);
    
    return `https://www.anrdoezrs.net/links/${cjId}/type/dlg/https://www.example.com/redirect?url=${encodedUrl}`;
  }

  static generateTrackingId() {
    return 'track_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
  }

  static async trackClick(trackingId, userId = null, metadata = {}) {
    try {
      // Record the click event
      await addDoc(collection(db, 'clickEvents'), {
        trackingId,
        userId,
        timestamp: new Date(),
        userAgent: navigator.userAgent,
        referrer: document.referrer,
        metadata
      });

      // Update the link tracking record
      const linkTrackingRef = doc(db, 'linkTracking', trackingId);
      await updateDoc(linkTrackingRef, {
        clicks: increment(1),
        lastClickedAt: new Date()
      });

      console.log('Click tracked successfully:', trackingId);
      
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  }

  static async trackConversion(trackingId, conversionValue = 0, orderId = null) {
    try {
      // Record the conversion event
      await addDoc(collection(db, 'conversionEvents'), {
        trackingId,
        orderId,
        conversionValue,
        timestamp: new Date(),
        source: 'artifex'
      });

      // Update the link tracking record
      const linkTrackingRef = doc(db, 'linkTracking', trackingId);
      await updateDoc(linkTrackingRef, {
        conversions: increment(1),
        revenue: increment(conversionValue),
        lastConversionAt: new Date()
      });

      console.log('Conversion tracked successfully:', trackingId, conversionValue);
      
    } catch (error) {
      console.error('Error tracking conversion:', error);
    }
  }

  static async getTrackingData(trackingId) {
    try {
      const linkTrackingRef = doc(db, 'linkTracking', trackingId);
      const docSnapshot = await getDoc(linkTrackingRef);
      
      if (docSnapshot.exists()) {
        return docSnapshot.data();
      } else {
        throw new Error('Tracking ID not found');
      }
    } catch (error) {
      console.error('Error getting tracking data:', error);
      return null;
    }
  }

  static async recordProductView(productId, contributorId, userId = null, source = 'shop') {
    try {
      await addDoc(collection(db, 'productViews'), {
        productId,
        contributorId,
        userId,
        source,
        timestamp: new Date(),
        userAgent: navigator.userAgent,
        referrer: document.referrer
      });

      // Update product view count
      const productRef = doc(db, 'contributorProducts', productId);
      await updateDoc(productRef, {
        views: increment(1),
        lastViewedAt: new Date()
      });

    } catch (error) {
      console.error('Error recording product view:', error);
    }
  }

  static async getContributorAnalytics(contributorId, dateRange = 30) {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - dateRange);

      // This would typically involve complex Firestore queries
      // For now, return placeholder data structure
      return {
        totalViews: 0,
        totalClicks: 0,
        totalConversions: 0,
        totalRevenue: 0,
        conversionRate: 0,
        topProducts: [],
        trafficSources: {
          shop: 0,
          editorial: 0,
          signals: 0,
          direct: 0
        },
        dateRange: {
          start: startDate,
          end: endDate
        }
      };
    } catch (error) {
      console.error('Error getting contributor analytics:', error);
      return null;
    }
  }

  // Attribution helpers
  static async attributeContentToCommerce(contentId, productId, interactionType) {
    try {
      await addDoc(collection(db, 'contentAttribution'), {
        contentId,
        productId,
        interactionType, // 'view', 'click', 'purchase'
        timestamp: new Date(),
        userId: null // Can be filled if user is logged in
      });
    } catch (error) {
      console.error('Error attributing content to commerce:', error);
    }
  }

  // Pixel tracking for external conversions
  static installConversionPixel() {
    // This would install tracking pixels for affiliate networks
    // that support server-to-server conversion tracking
    
    const pixelId = 'artifex-conversion-pixel';
    
    if (!document.getElementById(pixelId)) {
      const pixel = document.createElement('img');
      pixel.id = pixelId;
      pixel.style.display = 'none';
      pixel.width = 1;
      pixel.height = 1;
      
      // Example: Commission Junction conversion pixel
      // pixel.src = `https://www.emjcd.com/tags/c?containerTagId=YOUR_CONTAINER_TAG_ID&CID=${cjId}&OID=${orderId}&TYPE=1&AMOUNT=${amount}`;
      
      document.body.appendChild(pixel);
    }
  }

  // URL shortener for social sharing
  static async createShortenedUrl(originalUrl, contributorId, productId) {
    try {
      const trackingId = this.generateTrackingId();
      
      await addDoc(collection(db, 'shortenedUrls'), {
        trackingId,
        originalUrl,
        contributorId,
        productId,
        clicks: 0,
        createdAt: new Date()
      });

      return `${window.location.origin}/s/${trackingId}`;
      
    } catch (error) {
      console.error('Error creating shortened URL:', error);
      return originalUrl;
    }
  }

  // Revenue sharing calculations
  static calculateContributorCommission(saleAmount, contributorTier = 'standard') {
    const commissionRates = {
      standard: 0.10, // 10%
      premium: 0.12,  // 12%
      founding: 0.15  // 15%
    };

    const rate = commissionRates[contributorTier] || commissionRates.standard;
    return Math.round(saleAmount * rate * 100) / 100; // Round to 2 decimal places
  }

  // Link health checker
  static async checkLinkHealth(url) {
    try {
      const response = await fetch(url, { 
        method: 'HEAD',
        mode: 'no-cors' // May be limited by CORS policies
      });
      
      return {
        status: response.status,
        ok: response.ok,
        accessible: true
      };
    } catch (error) {
      return {
        status: null,
        ok: false,
        accessible: false,
        error: error.message
      };
    }
  }
}

export default AffiliateTracker;